import React, { Component } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
// @ts-ignore
import { Option, Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import {
  AdminService,
  ExecutionEnvironment,
  Mission as MissionDto,
  MissionType,
  PaymentRequestV1,
  ProviderDetails,
  ProviderService,
  User,
  UserService,
  UsersAdminService,
} from "../generated";
import { PaymentForm } from "./PaymentForm";
import { PaymentRequest } from "./PaymentRequest";
import { Field, Form, Formik, FormikProps } from "formik";
import { cloneDeep } from "lodash";
import traverse from "traverse";
import VrpConsentForm from "./VrpConsentForm";
import { VrpConsent } from "../generated/models/VRP/VrpConsent";
import {
  ExecutionEnv,
  ProdRegionsExecutionEnvtTypes,
  SpiirExecutionEnvType,
  SpiirRegionsExecutionEnvTypes,
} from "../generated/models/ExecutionEnvironment";
import { Redirect } from "react-router";
import { DEFAULT_AREA, Area, areas, NonUk, UkCountryCode } from "./admin/Area";
import { Policy } from "./policy";

export interface State {
  isOpenEndedMission: boolean;
  isLoading: boolean;
  userHash?: string;
  nagAccessToken?: string; // will be generated when user clicks on verification link, can manually be requested to e.g. easily test nag-app locally
  error?: any;
  rawReq?: Mission;
  currentBulkPayment?: number;
  providerDetails?: ProviderDetails;
  isProviderSearchPerformed?: boolean;
  isProviderSearchInProgress?: boolean;
  availableTesters: User[];
  useRegisteredTesterFlow: boolean;
  selectedTester: User;
  isInvited: boolean;
  execEnvironment: { id: ExecutionEnvironment; label: string }[];
}

export class Mission implements MissionDto {
  area: string;
  excludeProviders: Array<string>;
  executionEnvironment: ExecutionEnvironment;
  fetchAccountsCount: number;
  fetchTransactionCount: number;
  fetchTransactionDetails: boolean;
  fetchTransactionFromAccountNumber: string;
  fetchTransactionsSince: string;
  keepRequestResponseLogs: boolean;
  testerEmail: string;
  firstName: string;
  instruction: string;
  sendEmail: boolean;
  language: string;
  notes: string;
  paymentRequest: PaymentRequestV1;
  paymentRequests: Array<PaymentRequestV1>;
  providerId: string;
  providers: Array<string>;
  requestedBy: string;
  tags: Array<string>;
  theme: string;
  type: MissionType;
  consent: VrpConsent;
  useSmartFlow: boolean;
}

const CustomSelectComponent = (props: any) => (
  <select className="form-control" {...props} />
);

export class AdminCreateMission extends Component<{}, State> {
  private initialFormData: Mission;
  private formRef: React.RefObject<FormikProps<Mission>>;

  constructor(props: {}) {
    super(props);

    // This is not possible due to Google-Auth, but is allowed in CORS:
    // await fetch("https://nag-aoc-prod-ne.azurewebsites.net/api/db/providerconfigurations").then(x => x.json());

    this.state = {
      isOpenEndedMission: false,
      isLoading: true,
      userHash: null,
      nagAccessToken: null, // will be generated when user clicks on verification link, can manually be requested to e.g. easily test nag-app locally
      error: null,
      providerDetails: null,
      isProviderSearchPerformed: false,
      isProviderSearchInProgress: false,
      availableTesters: [],
      useRegisteredTesterFlow: true,
      selectedTester: null,
      isInvited: false,
      execEnvironment: ExecutionEnv,
    };
    this.submit = this.submit.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.addBulkPayment = this.addBulkPayment.bind(this);
    this.validateProviderId = this.validateProviderId.bind(this);
    this.formRef = React.createRef<FormikProps<Mission>>();

    this.initialFormData = {
      excludeProviders: [],
      fetchAccountsCount: "" as any as number,
      fetchTransactionCount: "" as any as number,
      fetchTransactionFromAccountNumber: "",
      fetchTransactionDetails: true,
      keepRequestResponseLogs: false,
      useSmartFlow: false,
      fetchTransactionsSince: "",
      language: "",
      firstName: "",
      testerEmail: "",
      notes: "",
      instruction: "",
      sendEmail: false,
      theme: "default",
      providerId: this.prefill("providerId"),
      requestedBy: null,
      area: null,
      providers: [],
      executionEnvironment: ExecutionEnv.find((env) => env.id === "Prod").id,
      tags: this.prefill("tags")
        .split(",")
        .filter((tag) => tag !== ""),
      type: MissionType.PAYMENTS,
      paymentRequest: new PaymentRequest(),
      paymentRequests: [],
      consent: null,
    };
  }

  async componentDidMount() {
    const r = await UserService.userIsInvited();

    this.setState({
      isLoading: false,
      isInvited: r,
    });

    const availableTesters = await UsersAdminService.usersAdminGetAll({
      withBanks: true,
    });

    this.setState({
      availableTesters,
      isLoading: false,
    });
  }

  async submit(result: Mission) {
    this.setState({
      isLoading: true,
    });
    const mission = cloneDeep(result);
    if (this.state.isOpenEndedMission) {
      mission.providerId = null;
      mission.tags = null;
    } else {
      mission.excludeProviders = null;
      mission.providers = null;
    }

    switch (mission.type) {
      case MissionType.ACCOUNTS:
        mission.paymentRequest = null;
        mission.paymentRequests = null;
        break;
      case MissionType.VRPCONSENT:
        mission.fetchTransactionsSince = null;
        mission.fetchTransactionFromAccountNumber = null;
        mission.fetchTransactionCount = null;
        mission.fetchAccountsCount = null;
        mission.fetchTransactionDetails = null;
        mission.consent.providerId = mission.providerId;

        break;
      // @ts-ignore
      case MissionType.BULK_PAYMENTS:
        mission.paymentRequest = null;
      // tslint:disable no-fallthrough
      case MissionType.PAYMENTS:
        mission.fetchTransactionsSince = null;
        mission.fetchTransactionFromAccountNumber = null;
        mission.fetchTransactionCount = null;
        mission.fetchAccountsCount = null;
        mission.fetchTransactionDetails = null;
        break;
    }

    traverse(mission).forEach(function (x) {
      if (x === "" || x === null) {
        delete this.parent.node[this.key];
      } else if (
        ["accountNumber", "ibanNumber", "creditorNumber"].indexOf(this.key) >= 0
      ) {
        this.update(x.toString().replaceAll(" ", ""), true);
      }
    });

    for (let i = 0; i < 3; i++) {
      traverse(mission).forEach(function (x) {
        if (
          x &&
          typeof x === "object" &&
          Object.keys(x).length === 0 &&
          this.key !== "destination"
        ) {
          delete this.parent.node[this.key];
        }
      });
    }

    const createMissionResponse = await AdminService.adminCreate({
      mission,
    });

    if (createMissionResponse.success) {
      this.setState({
        userHash: createMissionResponse.userHash,
        error: null,
        rawReq: mission,
      });
    } else {
      this.setState({
        userHash: null,
        error: createMissionResponse.error || createMissionResponse,
        rawReq: mission,
      });
    }
  }

  async getAccessToken() {
    const data = await ProviderService.providerAuthenticate({
      code: this.state.userHash,
    });

    this.setState({
      nagAccessToken: new URL(data.url).searchParams.get("startToken"),
    });
  }

  getRequest(successful = true) {
    return this.state.rawReq === null ? null : (
      <>
        <details>
          <summary>Raw request (debug)</summary>
          <pre>{JSON.stringify(this.state.rawReq, null, 4)}</pre>
          {successful && <br />}
          {successful &&
            (this.state.nagAccessToken ? (
              <input value={this.state.nagAccessToken} readOnly={true} />
            ) : (
              <button onClick={this.getAccessToken}>
                Start login and get login token
              </button>
            ))}
        </details>
      </>
    );
  }

  prefill(name: string) {
    let url = new URL(window.location.toString());
    return url.searchParams.get(name) || "";
  }

  addBulkPayment(values: Mission) {
    const bulkPayments = [...values.paymentRequests, new PaymentRequest()];
    values.paymentRequests = bulkPayments;

    this.setState({
      currentBulkPayment: bulkPayments.length - 1,
    });
  }

  showPayment(index: number) {
    this.setState({
      currentBulkPayment: index,
    });
  }

  private async resetEnvironmentField(
    missionType: MissionType,
    executionEnvironment: ExecutionEnvironment
  ) {
    if (
      missionType === MissionType.VRPCONSENT &&
      process.env.NODE_ENV === "production"
    ) {
      this.setState({
        execEnvironment: ExecutionEnv.concat(ProdRegionsExecutionEnvtTypes)
          .concat(SpiirExecutionEnvType)
          .concat(SpiirRegionsExecutionEnvTypes),
      });
    } else {
      this.setState({ execEnvironment: ExecutionEnv });
    }
    if (
      missionType !== MissionType.VRPCONSENT &&
      !ExecutionEnv.some((env) => env.id === executionEnvironment)
    ) {
      this.formRef.current.setFieldValue(
        "executionEnvironment",
        ExecutionEnv.find((env) => env.id === "Prod").id
      );
    }
  }

  private async tryToGetProviderDetails(
    providerId: string,
    tags: string[],
    executionEnvironment: ExecutionEnvironment,
    callback?: Function
  ) {
    this.setState(
      {
        providerDetails: null,
        isProviderSearchPerformed: false,
        isProviderSearchInProgress: true,
      },
      async () => {
        if (providerId) {
          try {
            const providerDetails = await AdminService.adminGetProviderDetails({
              environment: executionEnvironment,
              providerId: providerId,
              tags: tags,
            });

            AdminCreateMission.fixSchema(providerDetails);

            this.setState({
              providerDetails,
            });
          } catch {
            // ignore
          } finally {
            this.setState({
              isProviderSearchPerformed: true,
              isProviderSearchInProgress: false,
            });
          }
        }

        if (typeof callback === "function") {
          callback();
        }
      }
    );
  }

  private validateProviderId(value: string) {
    if (!this.state.isOpenEndedMission && !(value?.trim() ?? "")) {
      return "This field is required";
    }
    return undefined;
  }

  private extractCountryCodeFromProviders(provider: string) {
    const regex = /(\w+)_\w+/;
    const match = provider.match(regex);
    return match ? match[1] : null;
  }

  private validateProvidersArea(area: string, providers: Array<string>) {
    if (area === DEFAULT_AREA) {
      return (
        providers.filter(
          (provider) =>
            !!provider &&
            this.extractCountryCodeFromProviders(provider) !== UkCountryCode
        ).length > 0
      );
    } else {
      return (
        providers.filter(
          (provider) =>
            !!provider &&
            this.extractCountryCodeFromProviders(provider) === UkCountryCode
        ).length > 0
      );
    }
  }

  private setAreaField(
    setFieldValue: Function,
    values: Mission,
    resetArea: boolean = false
  ) {
    let countryCode = null;
    let areaValue = null;

    if (this.state.isOpenEndedMission) {
      areaValue = !resetArea && values.area ? values.area : DEFAULT_AREA;
    } else {
      if (this.state.isProviderSearchPerformed) {
        countryCode = this.extractCountryCodeFromProviders(values.providerId);
        if (this.state.useRegisteredTesterFlow) {
          const bank = this.state.availableTesters
            .find((tester) => tester.email === values.testerEmail)
            .banks.find((bank) => bank.id === values.providerId);

          if (bank) {
            countryCode = bank.countryCode;
          }
        }
      }

      if (countryCode) {
        areaValue = countryCode === UkCountryCode ? DEFAULT_AREA : NonUk.value;
      }
    }
    setFieldValue("area", areaValue);
  }

  render() {
    if (this.state.isInvited) {
      return <Redirect to={`/onboarding`} />;
    }

    if (this.state.userHash) {
      const url = `${window.location.origin}/start?code=${this.state.userHash}`;
      return (
        <div>
          <h3 className="pb-4 pt-4">Mission created</h3>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(url);
            }}
          >
            Copy URL to clipboard
          </Button>{" "}
          <Button
            onClick={() => {
              window.open(url);
            }}
          >
            Open URL
          </Button>
          {this.formRef.current.values.area && (
            <Policy
              area={this.formRef.current.values.area}
              missionType={this.formRef.current.values.type}
              providerId={this.formRef.current.values.providerId}
            />
          )}
          {this.getRequest()}
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div>
          <h3 className="pb-4 pt-4">Mission creation failed!</h3>
          <pre>{JSON.stringify(this.state.error, null, 4)}</pre>
          {this.getRequest(false)}
        </div>
      );
    }

    // @ts-ignore
    return (
      <div>
        <Modal
          isOpen={this.state.error != null}
          toggle={() => this.setState({ error: null })}
        >
          <ModalHeader>
            {this.state.error && this.state.error.title}
          </ModalHeader>
          <ModalBody>
            {this.state.error && JSON.stringify(this.state.error, null, 4)}
            {this.getRequest()}
          </ModalBody>
        </Modal>

        <Formik
          innerRef={this.formRef}
          initialValues={this.initialFormData}
          onSubmit={this.submit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Container>
              <Form className="pb-4 pt-4">
                <h3 className="pb-3">Create Mission</h3>

                <Row className="pb-4">
                  <Col md={12} lg={6}>
                    <ButtonGroup className={"w-100"}>
                      <Button
                        color="primary"
                        onClick={() => {
                          this.setState({ isOpenEndedMission: false }, () => {
                            setFieldValue("sendEmail", false);
                            this.resetEnvironmentField(
                              this.formRef.current.values.type,
                              this.formRef.current.values.executionEnvironment
                            );
                          });
                        }}
                        outline={this.state.isOpenEndedMission === true}
                        size="lg"
                      >
                        Single provider test
                      </Button>
                      <Button
                        color="primary"
                        onClick={() => {
                          this.setState(
                            {
                              isOpenEndedMission: true,
                              providerDetails: null,
                              isProviderSearchPerformed: false,
                              isProviderSearchInProgress: false,
                            },
                            () => {
                              setFieldValue("providerId", "");
                              setFieldValue("tags", []);
                              setFieldValue(
                                "paymentRequest",
                                new PaymentRequest()
                              );
                              setFieldValue("paymentRequests", []);
                              setFieldValue("sendEmail", false);
                              this.setAreaField(setFieldValue, values, true);
                            }
                          );
                          this.resetEnvironmentField(
                            this.formRef.current.values.type,
                            this.formRef.current.values.executionEnvironment
                          );
                        }}
                        outline={this.state.isOpenEndedMission === false}
                        size="lg"
                      >
                        Open-ended test
                      </Button>
                    </ButtonGroup>
                  </Col>
                  <Col md={12} lg={6}>
                    <ButtonGroup className={"w-100"}>
                      <Button
                        color="primary"
                        onClick={() => {
                          this.setState(
                            { useRegisteredTesterFlow: true },
                            () => {
                              setFieldValue("sendEmail", false);
                              this.resetEnvironmentField(
                                this.formRef.current.values.type,
                                this.formRef.current.values.executionEnvironment
                              );
                            }
                          );
                        }}
                        outline={this.state.useRegisteredTesterFlow === false}
                        size="lg"
                      >
                        Registered tester
                      </Button>
                      <Button
                        color="primary"
                        onClick={() => {
                          this.setState(
                            {
                              useRegisteredTesterFlow: false,
                            },
                            () => {
                              setFieldValue("sendEmail", false);
                              this.resetEnvironmentField(
                                this.formRef.current.values.type,
                                this.formRef.current.values.executionEnvironment
                              );
                            }
                          );
                        }}
                        outline={this.state.useRegisteredTesterFlow === true}
                        size="lg"
                      >
                        Anonymous tester
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12} lg={6}>
                    {(!this.state.isOpenEndedMission &&
                      ((this.state.useRegisteredTesterFlow &&
                        values.testerEmail) ||
                        !this.state.useRegisteredTesterFlow) && (
                        <Row>
                          <Col xs={12} md={5}>
                            <FormGroup>
                              <Label htmlFor={`providerId`}>Provider ID</Label>
                              {!this.state.useRegisteredTesterFlow && (
                                <Input
                                  name="providerId"
                                  type="text"
                                  tag={Field}
                                  placeholder="DK_Nordea"
                                  validate={this.validateProviderId}
                                  valid={
                                    !errors.providerId && touched.providerId
                                  }
                                  invalid={
                                    errors.providerId && touched.providerId
                                  }
                                />
                              )}
                              {this.state.useRegisteredTesterFlow && (
                                <Field
                                  name="providerId"
                                  as={CustomSelectComponent}
                                >
                                  <option value={""} />
                                  {this.state.availableTesters
                                    .find((x) => x.email === values.testerEmail)
                                    .banks.map((x) => (
                                      <option key={x.id} value={x.id}>
                                        {x.id}
                                      </option>
                                    ))}
                                </Field>
                              )}
                              {!this.state.useRegisteredTesterFlow && (
                                <FormText color="muted">
                                  Find this in{" "}
                                  <a href="https://nag-aoc-prod-ne.azurewebsites.net/provider">
                                    AOC
                                  </a>
                                  .
                                </FormText>
                              )}
                              {errors.providerId && touched.providerId && (
                                <FormFeedback>{errors.providerId}</FormFeedback>
                              )}
                            </FormGroup>
                          </Col>

                          <Col xs={12} md={5}>
                            <FormGroup>
                              <Label>Tags</Label>

                              <Typeahead
                                id="tags[]"
                                name="tags[]"
                                multiple
                                placeholder="master, rts-canary"
                                onChange={(listOfTags: Option[]) => {
                                  // listOfTags might be a list of strings (tags below)
                                  // ... or this weird data structure:
                                  // { "customOption": true, "label": "tagHere", "id": "new-id-1" }
                                  listOfTags = listOfTags.map((tag) => {
                                    if (typeof tag === "string") return tag;
                                    return (tag as Option).label;
                                  });
                                  setFieldValue("tags", listOfTags);
                                }}
                                allowNew={true}
                                options={[
                                  "billy-preferred",
                                  "bokoredo-preferred",
                                  "bolageriet-preferred",
                                  "canary",
                                  "danske-bank-preferred",
                                  "disabled",
                                  "dnb-preferred",
                                  "fallback",
                                  "fallback-disabled",
                                  "jyskebank-preferred",
                                  "kontolink-preferred",
                                  "laanekassen-poc",
                                  "licensed-fallback",
                                  "master",
                                  "mock",
                                  "monthio-preferred",
                                  "nordea-business-prerelease",
                                  "nordea-cards-test",
                                  "nordea-corporate-prerelease",
                                  "nordea-foretag-experiment",
                                  "op-checkout-preferred",
                                  "op-preferred",
                                  "rts",
                                  "rts-canary",
                                  "santander-preferred",
                                  "spiir-preferred",
                                  "swedbank-upgrade-test",
                                  "viia",
                                  "viia-test-preferred",
                                  "validation-test",
                                  "development",
                                  "pilot",
                                ]}
                              />
                              {/* all tags: find adapterconfig/ -name '*.yaml' -exec yq r {} 'tags' \; | tr ' ' '\n' | tr -d '[],"' | sort | uniq */}
                              <FormText color="muted">
                                Which tags to use for the test
                              </FormText>
                            </FormGroup>
                          </Col>

                          <Col xs={12} md={2}>
                            <Button
                              color={"primary"}
                              className={"mt-5"}
                              onClick={() => {
                                this.tryToGetProviderDetails(
                                  values.providerId,
                                  values.tags,
                                  values.executionEnvironment,
                                  () => {
                                    this.setAreaField(setFieldValue, values);
                                  }
                                );
                              }}
                              block={true}
                            >
                              🔍
                            </Button>
                          </Col>
                        </Row>
                      )) || (
                      <Row>
                        <Col xs={12}>
                          <Alert color={"info"}>
                            Select the tester first to continue!
                          </Alert>
                        </Col>
                      </Row>
                    )}
                    {!this.state.isOpenEndedMission &&
                      ((this.state.useRegisteredTesterFlow &&
                        values.testerEmail) ||
                        !this.state.useRegisteredTesterFlow) && (
                        <Row>
                          <Col xs={12} className={"pt-2"}>
                            {this.state.isProviderSearchPerformed &&
                              this.state.providerDetails && (
                                <Alert>
                                  Bank <b>{this.state.providerDetails.name}</b>{" "}
                                  found! Please proceed.
                                </Alert>
                              )}
                            {!this.state.isProviderSearchPerformed && (
                              <Alert color={"info"}>
                                Find the provider first to continue!
                              </Alert>
                            )}
                            {this.state.isProviderSearchPerformed &&
                              !this.state.providerDetails && (
                                <Alert color={"danger"}>
                                  No provider found! Make sure the tags are
                                  correct.
                                </Alert>
                              )}
                          </Col>
                        </Row>
                      )}
                    {this.state.isOpenEndedMission && (
                      <React.Fragment>
                        <Row>
                          <Col xs={12}>
                            <FormGroup
                              onBlur={() => {
                                this.setAreaField(setFieldValue, values);
                              }}
                            >
                              <Label>Area</Label>
                              <Field as={CustomSelectComponent} name="area">
                                {areas?.map((area: Area) => (
                                  <option key={area.id} value={area.value}>
                                    {area.name}
                                  </option>
                                ))}
                              </Field>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12}>
                            <FormGroup>
                              <Label>Limit Providers</Label>
                              <Field
                                name="providers"
                                placeholder="DK_Nordea, DK_DanskeBank"
                                as={Input}
                                type="text"
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  setFieldValue(
                                    "providers",
                                    e.target.value
                                      .split(",")
                                      .map((x: string) => x.trim())
                                  );
                                }}
                                validate={(value: string[]) => {
                                  if (
                                    this.validateProvidersArea(
                                      values.area,
                                      value
                                    )
                                  )
                                    return "Invalid provider area";
                                }}
                                valid={!errors.providers && touched.providers}
                                invalid={errors.providers && touched.providers}
                              />
                              {errors.providers && touched.providers && (
                                <FormFeedback>{errors.providers}</FormFeedback>
                              )}
                              <FormText color="muted">
                                Allow the tester to select between specific
                                providers {values.providers.length}
                              </FormText>
                            </FormGroup>
                          </Col>
                        </Row>
                      </React.Fragment>
                    )}
                    {this.state.isOpenEndedMission && (
                      <Row>
                        <Col xs={12}>
                          <FormGroup>
                            <Label>Exclude Providers</Label>
                            <Field
                              name="excludeProviders"
                              as={Input}
                              type="text"
                              placeholder="DK_Nordea, DK_DanskeBank"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setFieldValue(
                                  "excludeProviders",
                                  e.target.value
                                    .split(",")
                                    .map((x: string) => x.trim())
                                );
                              }}
                              validate={(value: string[]) => {
                                if (
                                  this.validateProvidersArea(values.area, value)
                                )
                                  return "Invalid provider area";
                              }}
                              valid={
                                !errors.excludeProviders &&
                                touched.excludeProviders
                              }
                              invalid={
                                errors.excludeProviders &&
                                touched.excludeProviders
                              }
                            />
                            {errors.excludeProviders &&
                              touched.excludeProviders && (
                                <FormFeedback>
                                  {errors.excludeProviders}
                                </FormFeedback>
                              )}
                            <FormText color="muted">
                              Prohibit the tester from selecting specific
                              providers
                            </FormText>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                  </Col>
                  <Col xs={12} md={6}>
                    {!this.state.useRegisteredTesterFlow && (
                      <Row>
                        <Col xs={12}>
                          <FormGroup>
                            <Label>First name of the tester</Label>
                            <Input
                              name="firstName"
                              type="text"
                              tag={Field}
                              placeholder="First name"
                            />
                            <FormText color="muted">
                              This is used for greeting the tester. Leave out
                              for a generic greeting
                            </FormText>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                    {this.state.useRegisteredTesterFlow &&
                      !this.state.isLoading && (
                        <Row>
                          <Col xs={12}>
                            <FormGroup>
                              <Label>Registered tester</Label>
                              <Field
                                name="testerEmail"
                                as={CustomSelectComponent}
                                onChange={(ev: Event) => {
                                  const email =
                                    Array.from(
                                      (ev.target as HTMLSelectElement).options
                                    ).find((x) => x.selected)?.value || null;
                                  const selectedTester =
                                    (email &&
                                      this.state.availableTesters.find(
                                        (x) => x.email === email
                                      )) ||
                                    null;
                                  setFieldValue("testerEmail", email);
                                  setFieldValue(
                                    "firstName",
                                    selectedTester?.firstName || ""
                                  );
                                  this.setState({ selectedTester });
                                }}
                              >
                                <option value={""}></option>
                                {this.state.availableTesters.map((x) => (
                                  <option key={x.email} value={x.email}>
                                    {x.lastName}&nbsp;{x.firstName}
                                  </option>
                                ))}
                              </Field>
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                    <Row>
                      <FormGroup>
                        <Label>Instruction</Label>
                        <Input
                          name="instruction"
                          type="text"
                          tag={Field}
                          placeholder="Please select only one account..."
                        />
                        <FormText color="muted">
                          Instruction for the tester, to be shown on the first
                          page
                        </FormText>
                      </FormGroup>
                      {!this.state.isOpenEndedMission &&
                      this.state.useRegisteredTesterFlow &&
                      this.state.selectedTester ? (
                        <Row>
                          <Col xs={12} md={6}>
                            <FormGroup>
                              <Label>
                                <Input
                                  type="checkbox"
                                  tag={Field}
                                  name="sendEmail"
                                />{" "}
                                Send e-mail
                              </Label>
                              <br />
                              <FormText color="muted">
                                Notify tester when a new test is created
                              </FormText>
                            </FormGroup>
                          </Col>
                        </Row>
                      ) : (
                        <Row>
                          <Col xs={12} md={6}>
                            <FormGroup>
                              <Label>
                                <Input
                                  disabled={true}
                                  type="checkbox"
                                  tag={Field}
                                  name="sendEmail"
                                  checked={false}
                                />{" "}
                                Send e-mail
                              </Label>
                              <br />
                              <FormText color="muted">
                                Notify tester when a new test is created
                              </FormText>
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                      <Col xs={12} md={6}>
                        <FormGroup>
                          <Label>
                            <Input
                              type="checkbox"
                              tag={Field}
                              name="useSmartFlow"
                            />{" "}
                            Use new Connect Platform
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <details className="pb-3">
                  <summary className="pb-1">Advanced settings</summary>
                  <FormGroup>
                    <Label>Notes</Label>
                    <Input
                      name="notes"
                      type="text"
                      tag={Field}
                      placeholder="Regression testing, ch22123"
                    />
                    <FormText color="muted">
                      Internal notes on why this test was performed, not shown
                      to tester
                    </FormText>
                  </FormGroup>

                  <Row>
                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label>Language</Label>
                        <Field
                          name="language"
                          placeholder="en"
                          as={CustomSelectComponent}
                        >
                          <option value={""}></option>
                          <option value={"en"}>English</option>
                          <option value={"no"}>Norwegian</option>
                          <option value={"da"}>Danish</option>
                          <option value={"sv"}>Swedish</option>
                          <option value={"fi"}>Finnish</option>
                        </Field>
                        <FormText color="muted">
                          Launch with a custom language
                        </FormText>
                      </FormGroup>
                    </Col>

                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label>Theme</Label>
                        <Field
                          name="theme"
                          placeholder="default"
                          as={CustomSelectComponent}
                        >
                          <option value={""}></option>
                          <option value={"default"}>default</option>
                          <option value={"spiir"}>spiir</option>
                          <option value={"checkoutfi"}>checkoutfi</option>
                        </Field>
                        <FormText color="muted">
                          Launch with a custom theme
                        </FormText>
                      </FormGroup>
                    </Col>

                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label>Execution Environment</Label>
                        <Field
                          name="executionEnvironment"
                          as={CustomSelectComponent}
                          onChange={(event: any) => {
                            setFieldValue(
                              "executionEnvironment",
                              event.target.value
                            );
                            this.tryToGetProviderDetails(
                              values.providerId,
                              values.tags,
                              event.target.value, //in case setFieldValue will not update in time
                              () => {
                                this.setAreaField(setFieldValue, values);
                              }
                            );
                          }}
                        >
                          {this.state.execEnvironment.map((env) => (
                            <option key={env.id} value={env.id}>
                              {env.label}
                            </option>
                          ))}
                        </Field>
                      </FormGroup>
                    </Col>
                  </Row>
                  <FormGroup>
                    <Label>
                      <Input
                        type="checkbox"
                        tag={Field}
                        name="keepRequestResponseLogs"
                      />{" "}
                      Keep request and response logs
                    </Label>
                    <br />
                    <FormText color="muted">
                      When enabled all requests and responses to the Aiia API
                      will be stored and available after the mission is
                      completed.
                    </FormText>
                  </FormGroup>
                </details>

                {(this.state.providerDetails ||
                  this.state.isOpenEndedMission) && (
                  <ButtonGroup className={"w-100 mb-3"}>
                    <Button
                      type={"button"}
                      color={"primary"}
                      size="lg"
                      outline={values.type !== MissionType.PAYMENTS}
                      onClick={() => {
                        setFieldValue("type", MissionType.PAYMENTS);
                        this.resetEnvironmentField(
                          MissionType.PAYMENTS,
                          values.executionEnvironment
                        );
                      }}
                    >
                      Single payment
                    </Button>
                    {this.state.providerDetails?.countryCode === "GB" && (
                      <Button
                        type={"button"}
                        color={"primary"}
                        size="lg"
                        outline={values.type !== MissionType.VRPCONSENT}
                        onClick={() => {
                          setFieldValue("type", MissionType.VRPCONSENT);
                          this.resetEnvironmentField(
                            MissionType.VRPCONSENT,
                            values.executionEnvironment
                          );
                        }}
                      >
                        VRP Consent
                      </Button>
                    )}
                    <Button
                      type={"button"}
                      color={"primary"}
                      size="lg"
                      outline={values.type !== MissionType.ACCOUNTS}
                      onClick={() => {
                        setFieldValue("type", MissionType.ACCOUNTS);
                        this.resetEnvironmentField(
                          MissionType.ACCOUNTS,
                          values.executionEnvironment
                        );
                      }}
                    >
                      Accounts & transactions
                    </Button>
                    <Button
                      type={"button"}
                      color={"primary"}
                      size="lg"
                      outline={values.type !== MissionType.BULK_PAYMENTS}
                      onClick={() => {
                        setFieldValue("type", MissionType.BULK_PAYMENTS);
                        this.resetEnvironmentField(
                          MissionType.BULK_PAYMENTS,
                          values.executionEnvironment
                        );
                      }}
                    >
                      Bulk payments
                    </Button>
                  </ButtonGroup>
                )}
                <Row>
                  {(this.state.providerDetails ||
                    this.state.isOpenEndedMission) &&
                    ((values.type === "Payments" && (
                      <PaymentForm
                        providerDetails={this.state.providerDetails}
                        tester={this.state.selectedTester}
                        providerId={values.providerId}
                        prependPath={"paymentRequest"}
                        hidden={values.type !== "Payments"}
                      />
                    )) ||
                      (values.type === MissionType.VRPCONSENT && (
                        <VrpConsentForm
                          CustomSelectComponent={CustomSelectComponent}
                          prependPath={"consent"}
                          providerId={values.providerId}
                          selectedTester={this.state.selectedTester}
                          providerDetails={this.state.providerDetails}
                          hidden={values.type !== MissionType.VRPCONSENT}
                        />
                      )) ||
                      (values.type === "Accounts" && (
                        <Container>
                          <Row>
                            <Col xs={12} md={6}>
                              <h4>Accounts</h4>
                              <FormGroup>
                                <Label>No. of accounts to fetch</Label>
                                <Input
                                  name="fetchAccountsCount"
                                  tag={Field}
                                  type="number"
                                />
                              </FormGroup>
                            </Col>
                            <Col xs={12} md={6}>
                              <h4>Transactions</h4>
                              <Row>
                                <Col xs={12} md={6}>
                                  <FormGroup>
                                    <Label>No. of transactions to fetch</Label>
                                    <Input
                                      name="fetchTransactionCount"
                                      tag={Field}
                                      type="number"
                                    />
                                  </FormGroup>
                                </Col>
                                <Col xs={12} md={6}>
                                  <FormGroup>
                                    <Label>Fetch transactions since</Label>
                                    <Input
                                      name="fetchTransactionsSince"
                                      tag={Field}
                                      type="date"
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                              <FormGroup>
                                <Label>
                                  Account number from which transactions should
                                  be taken
                                </Label>
                                <Input
                                  name="fetchTransactionFromAccountNumber"
                                  tag={Field}
                                  type="text"
                                />
                                <FormText color="muted">
                                  If not specified, an account will be chosen
                                  randomly
                                </FormText>
                              </FormGroup>
                              <FormGroup>
                                <Label>
                                  <Input
                                    type="checkbox"
                                    tag={Field}
                                    name="fetchTransactionDetails"
                                  />{" "}
                                  Fetch details
                                </Label>
                              </FormGroup>
                            </Col>
                          </Row>
                        </Container>
                      )) ||
                      (values.type === "BulkPayments" && (
                        <Container>
                          <Row>
                            <Col md={2}>
                              <ListGroup>
                                {values.paymentRequests.map(
                                  (payment, index) => {
                                    return (
                                      <ListGroupItem
                                        tag="button"
                                        type="button"
                                        key={"bulkPayment-" + index}
                                        active={
                                          this.state.currentBulkPayment ===
                                          index
                                        }
                                        onClick={() => this.showPayment(index)}
                                      >
                                        {"Payment #" + (index + 1)}
                                      </ListGroupItem>
                                    );
                                  }
                                )}
                              </ListGroup>
                              <Button
                                className="mt-3"
                                style={{ width: "100%" }}
                                color="primary"
                                tag="button"
                                type="button"
                                onClick={() => this.addBulkPayment(values)}
                              >
                                Add Payment
                              </Button>
                            </Col>
                            <Col md={10}>
                              {values.paymentRequests.map((payment, index) => {
                                return (
                                  <PaymentForm
                                    prependPath={`paymentRequests[${index}]`}
                                    pathResolver={(o) =>
                                      o.paymentRequests[index]
                                    }
                                    providerDetails={this.state.providerDetails}
                                    hidden={
                                      this.state.currentBulkPayment !== index ||
                                      values.type !== "BulkPayments"
                                    }
                                    key={"bulkPayment-" + index}
                                    tester={this.state.selectedTester}
                                    providerId={values.providerId}
                                  />
                                );
                              })}
                            </Col>
                          </Row>
                        </Container>
                      )))}
                </Row>

                <Row>
                  <Col>
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      {(this.state.providerDetails ||
                        this.state.isOpenEndedMission) && (
                        <Button type={"submit"} color="success">
                          Create mission
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
                {this.state.isLoading && <Spinner />}
                {(this.state.isOpenEndedMission ||
                  (this.state.isProviderSearchPerformed &&
                    this.state.providerDetails)) && (
                  <Policy
                    area={this.formRef.current.values.area}
                    missionType={this.formRef.current.values.type}
                    providerId={this.formRef.current.values.providerId}
                  />
                )}
              </Form>
            </Container>
          )}
        </Formik>
      </div>
    );
  }

  private static fixSchema(providerDetails: ProviderDetails) {
    if (
      typeof providerDetails?.paymentMethods === "object" &&
      providerDetails?.paymentMethods !== undefined
    ) {
      for (let name of Object.keys(providerDetails.paymentMethods)) {
        const paymentMethod = providerDetails.paymentMethods[name];

        if (paymentMethod.properties) {
          if (!paymentMethod.properties["amount.value"]) {
            paymentMethod.properties["amount.value"] = {
              required: true,
            };
          }

          if (!paymentMethod.properties["amount.currency"]) {
            paymentMethod.properties["amount.currency"] = {
              required: true,
            };
          }

          if (
            paymentMethod.properties["destination.bban"] &&
            !paymentMethod.properties["destination.bban.bankCode"]
          ) {
            paymentMethod.properties["destination.bban.bankCode"] = {
              required: !!paymentMethod.properties["destination.bban"].required,
            };
          }

          if (
            paymentMethod.properties["destination.bban"] &&
            !paymentMethod.properties["destination.bban.accountNumber"]
          ) {
            paymentMethod.properties["destination.bban.accountNumber"] = {
              required: !!paymentMethod.properties["destination.bban"].required,
            };
          }

          if (
            paymentMethod.properties["destination.iban"] &&
            !paymentMethod.properties["destination.iban.ibanNumber"]
          ) {
            paymentMethod.properties["destination.iban.ibanNumber"] = {
              required: !!paymentMethod.properties["destination.iban"].required,
            };
          }

          if (
            paymentMethod.properties["destination.inpaymentForm"] &&
            !paymentMethod.properties["destination.inpaymentForm.type"]
          ) {
            paymentMethod.properties["destination.inpaymentForm.type"] = {
              required:
                !!paymentMethod.properties["destination.inpaymentForm"]
                  .required,
            };
          }

          if (
            paymentMethod.properties["destination.inpaymentForm"] &&
            !paymentMethod.properties[
              "destination.inpaymentForm.creditorNumber"
            ]
          ) {
            paymentMethod.properties[
              "destination.inpaymentForm.creditorNumber"
            ] = {
              required:
                !!paymentMethod.properties["destination.inpaymentForm"]
                  .required,
            };
          }
        }
      }
    }
  }
}
